import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, Collapse, Typography } from '@mui/material';
import {
  type PropsWithChildren,
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useState,
} from 'react';

import {
  getLeftBorderColor,
  getLeftBorderWidth,
  LABEL_BG,
} from '../analyticalVariableCard.constants';

const NestingDepthContext = createContext(0);
export const useNestingDepth = () => useContext(NestingDepthContext);

interface CollapsibleLogicSectionProps {
  label: string;
  badge?: ReactNode;
  defaultOpen?: boolean;
  collapsible?: boolean;
  hasError?: boolean;
  'data-testid'?: string;
}

export const CollapsibleLogicSection = ({
  label,
  badge,
  defaultOpen = true,
  collapsible = true,
  hasError = false,
  children,
  'data-testid': dataTestId,
}: PropsWithChildren<CollapsibleLogicSectionProps>) => {
  const [open, setOpen] = useState(defaultOpen);
  const toggle = useCallback(() => setOpen((prev) => !prev), []);
  const depth = useNestingDepth();

  return (
    <Box
      data-testid={dataTestId}
      sx={{
        border: 1,
        borderColor: hasError ? 'error.main' : 'divider',
        borderLeft: hasError ? 3 : getLeftBorderWidth(depth),
        borderLeftColor: hasError ? 'error.main' : getLeftBorderColor(depth),
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      <Box
        role="button"
        tabIndex={0}
        aria-expanded={collapsible ? open : true}
        aria-label={label}
        data-testid={dataTestId ? `${dataTestId}-toggle` : undefined}
        onClick={collapsible ? toggle : undefined}
        onKeyDown={
          collapsible
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggle();
                }
              }
            : undefined
        }
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1.5,
          cursor: collapsible ? 'pointer' : 'default',
          bgcolor: LABEL_BG,
          ...(collapsible && { '&:hover': { bgcolor: 'grey.200' } }),
        }}
      >
        {badge}
        <Typography
          variant="body2"
          sx={{
            flex: 1,
            fontWeight: 600,
          }}
        >
          {label}
        </Typography>
        {collapsible && (
          <ChevronRightIcon
            fontSize="small"
            sx={{
              flexShrink: 0,
              transition: 'transform 0.2s',
              transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
            }}
          />
        )}
      </Box>
      <Collapse in={collapsible ? open : true}>
        <NestingDepthContext.Provider value={depth + 1}>
          <Box
            data-testid={dataTestId ? `${dataTestId}-content` : undefined}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              pl: 5,
              pr: 2,
              pt: 2,
              pb: 2,
            }}
          >
            {children}
          </Box>
        </NestingDepthContext.Provider>
      </Collapse>
    </Box>
  );
};
